import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import { maskStyle, Icon } from '../utils/icons';
import { useLang } from '../i18n';

const css = `
  .restabs { display: flex; gap: 12px; padding: 10px 16px 6px; }
  .restab { flex: 1; height: 40px; border-radius: 8px; background: #1d1c3c; color: #fff; font-size: 14px; font-weight: 600; }
  .restab.is-active { background: linear-gradient(90deg, #8187f6, #9aa0f9); }

  .resfilters { padding: 4px 16px 14px; }
  .resfilters-labels { display: flex; gap: 10px; margin-bottom: 6px; }
  .resfilters-labels span { font-size: 12px; color: #9b9ab0; }
  .resfilters-row { display: flex; gap: 10px; align-items: stretch; }
  .resfield { height: 38px; border-radius: 8px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.09); display: flex; align-items: center; gap: 8px; padding: 0 12px; font-size: 13px; color: #fff; white-space: nowrap; }
  .resfield.is-select { justify-content: space-between; }
  .field-caret { width: 0; height: 0; flex: none; border-left: 4px solid transparent; border-right: 4px solid transparent; border-top: 5px solid #9b9ab0; }
  .lead-btn { height: 38px; padding: 0 24px; border-radius: 8px; flex: none; background: linear-gradient(90deg, #8187f6, #9aa0f9); color: #fff; font-size: 13px; font-weight: 600; }

  .comp { box-shadow: inset 0 -1px 0 rgba(255,255,255,.06); }
  .comp-head { width: 100%; display: flex; align-items: center; gap: 12px; height: 48px; padding: 0 16px; text-align: left; }
  .comp-emblem { width: 18px; height: 18px; flex: none; }
  .comp-name { font-size: 15px; font-weight: 500; color: #fff; }
  .comp-chevron { margin-left: auto; width: 12px; height: 7px; background: #9b9ab0; transform: rotate(90deg); transition: transform .2s ease; }
  .comp.is-open .comp-chevron { transform: rotate(-90deg); }
  .comp.is-open { background: rgba(255,255,255,.015); }
  .comp-body { display: none; }
  .comp.is-open .comp-body { display: block; }

  .result-row { padding: 12px 16px 0; }
  .result-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; height: 24px; }
  .result-team { font-size: 14px; color: #fff; }
  .result-score { font-size: 14px; font-weight: 700; color: #fff; }
  .result-ht { font-size: 13px; color: #9b9ab0; margin: 2px 0 0; }
  .result-meta { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding: 8px 0; box-shadow: inset 0 1px 0 rgba(255,255,255,.06); }
  .result-date { font-size: 12px; color: #9b9ab0; }
  .result-view { margin-left: auto; display: flex; align-items: center; gap: 5px; font-size: 13px; color: #76e1ff; cursor: pointer; background: none; border: none; }
  .result-vchev { width: 5px; height: 9px; background: #76e1ff; }
`;

const AFC_RESULTS = [
  { home: 'Kawasaki Frontale', away: 'Shanghai Port FC', hs: '3', as: '1', ht: '3:1 (3:0)', date: '05.11.2024 | 14:00' },
  { home: 'Vissel Kobe', away: 'Gwangju FC', hs: '2', as: '0', ht: '2:0 (1:0)', date: '05.11.2024 | 12:00' },
  { home: 'Central Coast Mariners', away: 'Shanghai Greenland Shenhua', hs: '2', as: '2', ht: '2:2 (0:0)', date: '05.11.2024 | 14:00' },
];

const COMPETITIONS = [
  { name: 'AFC Champions League Elite', color: '#3aa0ff', results: AFC_RESULTS },
  { name: 'Calcutta First Division', color: '#8b8a9e' },
  { name: 'Campeonato Minerio Segunda Divisao', color: '#8b8a9e' },
  { name: 'Coppa Italia - Women', color: '#3ac0d0' },
  { name: 'FAI Intermediate Cup', color: '#8b8a9e' },
  { name: 'Fastex Premier League', color: '#e0407f' },
  { name: 'La Liga', color: '#e23b3b' },
];

function Emblem({ color }) {
  return (
    <svg className="comp-emblem" viewBox="0 0 18 18" fill="none">
      <path d="M9 1l6 2v5c0 4-2.6 7-6 9-3.4-2-6-5-6-9V3z" fill={color} opacity="0.9" />
    </svg>
  );
}

function ResultRow({ r }) {
  const { t } = useLang();
  const navigate = useNavigate();
  return (
    <div className="result-row">
      <div className="result-line"><span className="result-team">{r.home}</span><span className="result-score">{r.hs}</span></div>
      <div className="result-line"><span className="result-team">{r.away}</span><span className="result-score">{r.as}</span></div>
      <p className="result-ht">{r.ht}</p>
      <div className="result-meta">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="5" stroke="#9b9ab0" strokeWidth="1.1" />
          <path d="M6 3.2V6l2 1.3" stroke="#9b9ab0" strokeWidth="1.1" strokeLinecap="round" />
        </svg>
        <span className="result-date">{r.date}</span>
        <button className="result-view" onClick={() => navigate('/match-result')}>
          {t('results.view')} <span className="result-vchev" style={maskStyle('ic-chevron')} />
        </button>
      </div>
    </div>
  );
}

function CompetitionAccordion({ comp, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section className={'comp' + (open ? ' is-open' : '')}>
      <button className="comp-head" onClick={() => setOpen(!open)}>
        <Emblem color={comp.color} />
        <span className="comp-name">{comp.name}</span>
        <span className="comp-chevron" style={maskStyle('ic-chevron')} />
      </button>
      <div className="comp-body">
        {(comp.results || []).map((r, i) => <ResultRow key={i} r={r} />)}
      </div>
    </section>
  );
}

function ResFilters({ mode, t }) {
  return (
    <div className="resfilters">
      <div className="resfilters-labels">
        <span style={{ flex: mode === 'finished' ? 2 : 1 }}>{mode === 'finished' ? t('results.range') : 'Sports'}</span>
        <span style={{ flex: 1.3 }}>{t('results.competition')}</span>
        <span style={{ width: 80 }} />
      </div>
      <div className="resfilters-row">
        {mode === 'finished' ? (
          <>
            <div className="resfield" style={{ flex: 1 }}>
              05/07/2024 <Icon name="ic-calendar" w={13} h={13} color="#9b9ab0" />
            </div>
            <div className="resfield" style={{ flex: 1 }}>
              05/07/2024 <Icon name="ic-calendar" w={13} h={13} color="#9b9ab0" />
            </div>
          </>
        ) : (
          <div className="resfield" style={{ flex: 1 }}>Football</div>
        )}
        <div className="resfield is-select" style={{ flex: 1.3 }}>All<span className="field-caret" /></div>
        <button className="lead-btn">{t('results.lead')}</button>
      </div>
    </div>
  );
}

export default function Results() {
  const { t } = useLang();
  const [tab, setTab] = useState('live');

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <div className="restabs">
        <button className={'restab' + (tab === 'live' ? ' is-active' : '')} onClick={() => setTab('live')}>{t('results.live')}</button>
        <button className={'restab' + (tab === 'finished' ? ' is-active' : '')} onClick={() => setTab('finished')}>{t('results.finished')}</button>
      </div>
      <ResFilters mode={tab} t={t} />
      <div>
        {COMPETITIONS.map((c, i) => (
          <CompetitionAccordion key={tab + i} comp={c} defaultOpen={i === 0} />
        ))}
      </div>
    </PhoneShell>
  );
}
